import { Component, OnInit, OnDestroy, ViewChild } from "@angular/core";
import { Location } from "@angular/common";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

import { ActivatedRoute, Router, Params } from "@angular/router";
import { DownloadFileComponent } from "../downloadfile/downloadfile.component";
import { CountryInformationService } from "../../services/countryinformation.service";
import { AuditHistoryService } from "../../services/audithistory.service";
import { DownloadService } from "../../services/download.service";
import { AuthService } from "../../services/auth.service";
import { ICountry } from "../../interfaces/icountry";
import { IExpandableItemConfigSet, IItemConfigSet, IItemConfig } from "../../interfaces/iconfigurationset";
import { IPublicationHistory } from "../../interfaces/ipublicationhistory";
import { Features } from "../../enums/features";

@Component({
    selector: "countryinformation",
    templateUrl: "./countryinformation.component.html",
    standalone: false
})
export class CountryInformationComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();
    countryCode!: string;
    categoryId!: number;
    countryInformation!: IItemConfigSet;
    countryHistoryList!: IExpandableItemConfigSet[];
    selectedCategory!: IItemConfig | null;
    country!: ICountry | null;
    countriesWithCategoryDetail!: ICountry[];
    countryDropdownItems: any[] = new Array();
    selectedCountry: any = null;
    selectedCategoryItem: any[] = new Array();
    selectedPublication: any[] = new Array();
    invalidCountryCode = false;
    showNoContentMessage: boolean = false;
    downloadingContent: boolean = false;
    showHistory: boolean = false;
    features = Features;
    @ViewChild("download") fileDownload!: DownloadFileComponent;
    isLoading: boolean = false;
    constructor(
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private countryInformationService: CountryInformationService,
        private auditHistoryService: AuditHistoryService,
        private location: Location,
        private downloadSvc: DownloadService
    ) { }

    ngOnInit(): void {
        this.downloadingContent = false;
        this.activatedRoute.params
            .pipe(takeUntil(this.destroy$))
            .subscribe((params: Params) => {
                this.countriesWithCategoryDetail = this.activatedRoute.snapshot.data['countriesWithCategoryDetail'];
                if (this.countriesWithCategoryDetail.length === 0) {
                    return;
                }
                this.invalidCountryCode = false;
                this.downloadingContent = false;

                this.countryCode = params['countryCode'];

                this.categoryId = params['categoryId'] || parseInt(params['categoryId'], 10);

                if (this.countryCode === undefined) {
                    this.countryCode = this.countriesWithCategoryDetail[0].code;
                }

                if (!this.countriesWithCategoryDetail.find((country: ICountry) => country.code === this.countryCode)) {
                    this.router.navigate([`/${AuthService.businessFunction?.defaultRoute || ""}/countryinformation`]);
                }

                this.countriesWithCategoryDetail.map((country: ICountry) => {
                    const dropDownItem: any = {
                        id: country.code,
                        text: country.shortName,
                        flagClass: "flag flag--" + country.code.toLowerCase()
                    };
                    this.countryDropdownItems.push(dropDownItem);
                    if (country.code === this.countryCode) {
                        this.selectedCountry = dropDownItem.id;
                    }
                });

                this.getCountryInformation();
            });
    }

    /**
     * Gets country information based on the property @categoryCode which is set ont he first load
     * or when the countries dropdown os selected
     */
    getCountryInformation(): void {
        this.isLoading = true;
        this.countryInformationService.getCountryInformation(this.countryCode)
            .pipe(takeUntil(this.destroy$))
            .subscribe((ci) => {
                this.countryInformation = ci;
                // Set the country from the countriesWithCategoryDetail array
                this.country = this.countriesWithCategoryDetail.find(c => c.code === this.countryCode) || null;
                this.showHistory = false;
                if (this.categoryId !== undefined) {
                    this.selectedCategory = this.countryInformation.itemConfigs.find(item => item.id === this.categoryId) || null;

                    // select first category if category or content for a category cannot be found
                    if (!this.selectedCategory || !this.selectedCategoryItem) {
                        this.onCategoryClick(this.countryInformation.itemConfigs[0]);
                    }
                } else {
                    this.onCategoryClick(this.countryInformation.itemConfigs[0]);
                }
                this.isLoading = false;
            }, () => {
                this.isLoading = false;
                this.invalidCountryCode = true;
            });
    }

    /**
     * Gets country information based on the property @categoryCode which is set ont he first load
     * or when the countries dropdown os selected
     */
    getCountryHistoryList(): void {
        this.auditHistoryService.getCountryHistoryList(this.countryCode)
            .pipe(takeUntil(this.destroy$))
            .subscribe((history) => {
                this.countryHistoryList = history as IExpandableItemConfigSet[];
            }, () => {
                this.invalidCountryCode = true;
            });
    }

    /**
     * performs the download of the files
     * @param fileName name to give the file to download
     * @param response the response object to set as the attachment
     */
    performDownload(fileName: string, response: any, extension: string, type: string): void {
        this.fileDownload.downloadFile(fileName, response, extension, type);
    }

    downloadFile(): void {
        var date: Date = new Date(Date.now());
        var dateString: string = date.getFullYear() + "-" + ("0" + (date.getMonth() + 1)).slice(-2) + "-" + ("0" + date.getDate()).slice(-2);
        this.downloadingContent = true;

        this.downloadSvc.downloadCountryInformation(this.countryCode)
            .pipe(takeUntil(this.destroy$))
            .subscribe((res: any) => {
                this.downloadingContent = false;
                this.performDownload(`${this.countryCode}_summary_${dateString}`, res, "pdf", "application/octet-stream");
            }, () => {
                this.downloadingContent = false;
            });
    }
    toggleHistoryInformation(): void {
        this.showHistory = !this.showHistory;
        if (this.showHistory) {
            this.getCountryHistoryList();
        }
    }

    /**
     * Gets called when a category changes
     * @param category the category being selected
     */
    onCategoryClick(category: IItemConfig): void {
        if (category) {
            this.selectedCategory = this.countryInformation.itemConfigs.find(item => item.id === parseInt(category.id.toString(), 10)) || null;
            const route: string = `${AuthService.businessFunction?.defaultRoute}/countryinformation/${this.countryCode}/${category.id}`;
            this.location.replaceState(route);
            window.scrollTo(0, 0);
        }
    }

    /**
     * Gets called when a category changes
     * @param category the category being selected
     */
    onPublicationClick(publication: IExpandableItemConfigSet): void {
        if (!publication.publicationHistory) {
            this.auditHistoryService.getPublication(this.countryCode, publication.countryDataId.toString())
                .pipe(takeUntil(this.destroy$))
                .subscribe(
                    (contents: IPublicationHistory[]) => {
                        publication.publicationHistory = contents;
                    });
        }
        publication.expanded = !publication.expanded;
    }

    /**
     * Gets called when a country is selected via the drop down
     * @param countrySelected The country selected
     *  the gets country inforamtion for that country
     */
    onCountrySelected(countrySelected: any): void {

        this.countryCode = countrySelected;
        this.getCountryInformation();
        window.scrollTo(0, 0);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}